import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Receipt, Search, RefreshCw, Ban, Printer, Download } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePermissions } from '../contexts/PermissionsContext';
import Layout from '../components/Layout';
import PageHeader from '../components/layout/PageHeader';
import { printTicket80mm, downloadAvoirTicketPdf } from '../utils/ticket80mmPrint';

// Statuts indexés sur les valeurs backend FR (codes) — libellés via clés i18n, repli sur le code brut.
const STATUT_META = {
  disponible: { labelKey: 'avoirs.statutDisponible', cls: 'bg-emerald-950/40 text-emerald-300 border-emerald-500/30' },
  partiellement_utilise: { labelKey: 'avoirs.statutPartiel', cls: 'bg-amber-950/40 text-amber-300 border-amber-500/30' },
  utilise: { labelKey: 'avoirs.statutUtilise', cls: 'bg-gray-800/60 text-gray-400 border-white/10' },
  annule: { labelKey: 'avoirs.statutAnnule', cls: 'bg-red-950/40 text-red-300 border-red-500/30' },
};

const AvoirsPage = () => {
  const { api, user } = useAuth();
  const { t, formatCurrency, formatDate } = useLanguage();
  const { isSuper, isAdminMag } = usePermissions();
  const canCancel = isSuper || isAdminMag || user?.username === 'carlos' || user?.role === 'admin';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statut, setStatut] = useState('');
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');

  const fmt = (n) => formatCurrency(Number(n) || 0);
  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return `${formatDate(d, { year: 'numeric', month: '2-digit', day: '2-digit' })} ${formatDate(d, { hour: '2-digit', minute: '2-digit' })}`;
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/avoirs', { params: { q: q || undefined, statut: statut || undefined, limit: 200 } });
      setRows(res.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || t('avoirs.errorLoad'));
    } finally {
      setLoading(false);
    }
  }, [api, q, statut, t]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (ref) => {
    try {
      const res = await api.get(`/avoirs/${encodeURIComponent(ref)}`);
      setDetail(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    }
  };

  const cancelAvoir = async (ref) => {
    if (!window.confirm(t('avoirs.confirmCancel', { ref }))) return;
    try {
      await api.post(`/avoirs/${encodeURIComponent(ref)}/cancel`, { motif: 'Annulation staff' });
      setDetail(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    }
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 max-w-6xl mx-auto">
        <PageHeader
          title={t('avoirs.title')}
          subtitle={t('avoirs.subtitle')}
          icon={Receipt}
          backTo="/activite?tab=ventes"
          actions={(
            <button type="button" onClick={load} className="rm-touch rounded-xl border px-3 py-2 text-sm text-gray-200" style={{ borderColor: 'var(--rm-border-subtle)' }}>
              <RefreshCw className="w-4 h-4 inline mr-1" /> {t('avoirs.refresh')}
            </button>
          )}
        />

        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('avoirs.searchPlaceholder')}
              className="input-dark w-full pl-9 py-3 min-h-[44px]"
            />
          </div>
          <select value={statut} onChange={(e) => setStatut(e.target.value)} className="input-dark py-3 min-h-[44px] sm:w-56">
            <option value="">{t('avoirs.allStatuses')}</option>
            {Object.entries(STATUT_META).map(([k, v]) => (
              <option key={k} value={k}>{t(v.labelKey)}</option>
            ))}
          </select>
        </div>

        {error && <p className="text-sm text-red-400 mb-3">{error}</p>}

        <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-elevated)' }}>
          {loading ? (
            <div className="p-8 text-center"><div className="loading-spinner mx-auto" /></div>
          ) : rows.length === 0 ? (
            <p className="p-8 text-center text-gray-500">{t('avoirs.empty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm table-dark-custom">
                <thead>
                  <tr>
                    <th>{t('avoirs.colRef')}</th>
                    <th>{t('avoirs.colClient')}</th>
                    <th>{t('avoirs.colOrigin')}</th>
                    <th>{t('avoirs.colInitial')}</th>
                    <th>{t('avoirs.colSolde')}</th>
                    <th>{t('avoirs.colStatut')}</th>
                    <th>{t('avoirs.colDate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => {
                    const meta = STATUT_META[a.statut] || STATUT_META.disponible;
                    return (
                      <tr key={a.id} className="cursor-pointer hover:bg-white/5" onClick={() => openDetail(a.ref)}>
                        <td className="font-mono font-bold">{a.ref}</td>
                        <td>{a.client_nom || '—'}{a.client_tel ? ` · ${a.client_tel}` : ''}</td>
                        <td className="font-mono text-xs">{a.source_ref}</td>
                        <td>{fmt(a.montant_initial)}</td>
                        <td className="font-bold text-emerald-400">{fmt(a.solde)}</td>
                        <td><span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border ${meta.cls}`}>{t(meta.labelKey)}</span></td>
                        <td className="text-xs text-gray-500">{fmtDate(a.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {detail && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
          <div className="modal-dark w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="modal-dark-header flex items-center justify-between">
              <h3 className="font-bold font-mono">{detail.ref}</h3>
              <button type="button" onClick={() => setDetail(null)} className="text-gray-400" aria-label={t('avoirs.close')}>✕</button>
            </div>
            <div className="p-6 space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>{t('avoirs.colInitial')} <strong className="block">{fmt(detail.montant_initial)}</strong></div>
                <div>{t('avoirs.colSolde')} <strong className="block text-emerald-400">{fmt(detail.solde)}</strong></div>
                <div>{t('avoirs.colStatut')} <strong className="block">{STATUT_META[detail.statut] ? t(STATUT_META[detail.statut].labelKey) : detail.statut}</strong></div>
                <div>{t('avoirs.createdBy')} <strong className="block">{detail.created_by || '—'}</strong></div>
              </div>
              <p className="text-gray-400">{t('avoirs.originLabel')} <span className="font-mono text-white">{detail.source_ref}</span> ({detail.source_type})</p>
              {detail.motif && <p>{t('avoirs.motifLabel')} {detail.motif}</p>}
              <p>{t('avoirs.clientLabel')} {detail.client_nom || '—'} {detail.client_tel || ''}</p>
              <button
                type="button"
                onClick={() => printTicket80mm(api, { type: 'avoir', ref: detail.ref }).catch((err) => alert(err.response?.data?.detail || err.message))}
                className="w-full py-3 rounded-xl border font-semibold text-gray-200"
                style={{ borderColor: 'var(--rm-border-subtle)' }}
              >
                <Printer className="w-4 h-4 inline mr-1" /> {t('avoirs.printTicket')}
              </button>
              <button
                type="button"
                onClick={() => downloadAvoirTicketPdf(api, detail.ref).catch((err) => alert(err.response?.data?.detail || err.message))}
                className="w-full py-3 rounded-xl border font-semibold text-gray-200"
                style={{ borderColor: 'var(--rm-border-subtle)' }}
                data-testid="avoir-download-pdf"
              >
                <Download className="w-4 h-4 inline mr-1" /> {t('avoirs.downloadPdf')}
              </button>
              <h4 className="font-bold pt-2">{t('avoirs.historyTitle')}</h4>
              <ul className="space-y-2">
                {(detail.mouvements || []).map((m) => (
                  <li key={m.id} className="rounded-lg border p-2" style={{ borderColor: 'var(--rm-border-subtle)' }}>
                    <div className="flex justify-between">
                      <span className="uppercase text-[10px] text-gray-500">{m.type}</span>
                      <span className={Number(m.montant) < 0 ? 'text-red-400' : 'text-emerald-400'}>{fmt(m.montant)}</span>
                    </div>
                    <div className="text-[11px] text-gray-500">
                      {fmtDate(m.created_at)} · {m.created_by}
                      {m.vente_ref ? (
                        <>
                          {' · '}
                          <Link className="underline" to={`/activite?tab=ventes&q=${encodeURIComponent(m.vente_ref)}`}>{m.vente_ref}</Link>
                        </>
                      ) : null}
                    </div>
                    {m.note && <p className="text-xs text-gray-400 mt-1">{m.note}</p>}
                    <p className="text-[11px] text-gray-600">{t('avoirs.colSolde')} {fmt(m.solde_avant)} → {fmt(m.solde_apres)}</p>
                  </li>
                ))}
              </ul>
              {canCancel && detail.statut !== 'annule' && Number(detail.solde) > 0 && (
                <button type="button" onClick={() => cancelAvoir(detail.ref)} className="w-full py-3 rounded-xl border text-red-300 font-semibold" style={{ borderColor: 'var(--rm-red-muted)' }}>
                  <Ban className="w-4 h-4 inline mr-1" /> {t('avoirs.cancelRemaining')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AvoirsPage;
